import React, { useState, useEffect } from 'react';
import { areEqual, isValidEmail } from '../../util';
import CloseIcon from '@material-ui/icons/Close';
import Toggle from 'react-toggle';
import DatePicker from '../../components/shared/DatePicker/DatePicker';
import './selected-supplier-module.css';
import StyledCheckbox from './../../components/shared/StyledCheckbox/StyledCheckbox';
import BtButton from '../bt-button/bt-button';
import Selector from '../../components/shared/Selector/Selector';
import { useTranslation, Trans } from 'react-i18next';

const SelectedSupplierModule = React.memo(
  ({
    contactPersons,
    selectedSuppliers,
    onRemoveSupplier,
    quoteVersions,
    onSelectContactPerson,
    onToggleQuotation,
    handleQuoteExpiry,
    extendedDate,
    checkQuotationExpiry,
    onSaveSelectedSuppliersClick,
    showLoading,
    errorMsg,
  }) => {
    const [sendAutoBid, setSendAutoBid] = useState(quoteVersions?.length > 0);
    const [t] = useTranslation('supplierDatabase', { keyPrefix: 'selectMode.selectedSuppliers' });

    const handleQuotationToggle = () => {
      setSendAutoBid(prevState => {
        onToggleQuotation(!prevState);
        return !prevState
      });
    };

    useEffect(() => {
      setSendAutoBid(quoteVersions?.length > 0);
      onToggleQuotation(quoteVersions?.length > 0);
    }, [quoteVersions?.length > 0]);

    const renderDropdown = sup => {
      const selectedContact = contactPersons.find(mem => mem.supplierId === sup.sup_id)?.supplierMember || sup.supMember[0];
      return sup.supMember?.length > 0 && sendAutoBid && (
        <div className="contact-person">
          <span className="color-gray-700">{t('selectedContact.label', 'Kontaktperson:')}</span>
          <Selector
            className="selectSuppliersDropdown"
            placeholder={t('selectedContact.placeholder', 'Välj kontaktperson')}
            options={
              sup.supMember
                .filter(supMember => isValidEmail(supMember.email))
                .map(supMember => ({
                label: supMember.name,
                value: supMember,
              }))
            }
            value={{ label: selectedContact.name, value: selectedContact }}
            onChange={e => onSelectContactPerson(e.value)}
          />
        </div>
      );
    };

    const renderQuoteToggle = () => (
      quoteVersions?.length > 0 && selectedSuppliers?.length > 0 && (
        <div className="autoBid-toggle">
          <p>{t('sendAutoBid.heading', 'Förfrågningsunderlag')}</p>
          <label className="autoBid-toggle-label">
            <Toggle
              disabled={!selectedSuppliers?.length}
              className="ar-checkbox-toggle autobid-switch"
              checked={sendAutoBid}
              onChange={handleQuotationToggle}
              icons={false}
            />
            <p style={{ fontSize: '14px', color: 'var(--gray-700)' }}>{t('sendAutoBid.label', 'Skicka senaste förfrågningsunderlaget automatiskt')}</p>
          </label>
        </div>
      )
    );

    const renderDateExtend = () => {
      let exp_check = checkQuotationExpiry();
      const _extDate = extendedDate ? new Date(extendedDate) : null;

      return quoteVersions?.length > 0 && exp_check && sendAutoBid && (
        <div className="autoQuoteCheckbox">
          <div className="date-extend-container">
            <p>
              {t('extendQuotation.label', 'Välj nytt utgångsdatum')}<br/>
              <span>{t('extendQuotation.intro', 'Utgångsdatumet för förfrågningsunderlaget har gått ut. Du kan ändra datumet här.')}</span>
            </p>
            <div className="date-extend-sub-container">
              <DatePicker
                value={_extDate}
                selected={_extDate}
                minDate={new Date()}
                popperPlacement="top-end"
                popperClassName="column-date-end-calendar-position-fix"
                onChange={handleQuoteExpiry}
                style={{ color: 'var(--gray-700)' }}
              />
              { errorMsg && <div style={{ color: 'red', marginTop: '16px', fontSize: '14px' }}>{ errorMsg }</div> }
            </div>
          </div>
        </div>
      );
    };

    const handleRemoveSup = sup => {
      onRemoveSupplier(null, sup);
    };

    const renderSuppliers = sup => (
      <div className="selected-supplier-mod" key={sup.sup_id}>
        <div className="sup_name_head">
          <StyledCheckbox checked onClick={() => handleRemoveSup(sup)} />
          <div className="sup_name">
            {sup.sup_name}
            {sup.isRecommended && <span> {t('suppliers.supplierWillBeAddedToDatabase', '(läggs till i leverantörsdatabasen)')}</span>}
          </div>
        </div>
        <div className="icon-dropdown-container">
          {renderDropdown(sup)}
          <div className='selectedSupplierCloseDiv' onClick={() => handleRemoveSup(sup)}>
            <span>{t('suppliers.remove', 'Ta bort')}</span>
            <CloseIcon className="supList-remove-icon" htmlColor='var(--gray-500)' />
          </div>
        </div>
      </div>
    );

    const ValdComponent = React.memo(() => {
      const count = selectedSuppliers.length;
      return <span className="vald">{
        count ? (
          <Trans t={t} i18nKey="selectedSuppliersCounter" count={count}>
            <b>{{count}}</b> valda
          </Trans>
        ) : t('noSelectedSuppliers', 'Inga valda leverantörer')
      }</span>;
    });
    ValdComponent.displayName = 'ValdComponent';

    return (<>
      <div className="selected-supplier-mod-head">
        <div>
          {t('heading', 'Valda leverantörer')}
          <ValdComponent />
        </div>
      </div>

      {selectedSuppliers?.length ? (
        <div style={{display:'flex', flexDirection:'column', gap:'16px'}}>
          {selectedSuppliers.map(sup => renderSuppliers(sup))}
        </div>
      ) : (
        <span style={{ fontSize: '14px' }}>{t('emptyMessage', 'Dina valda leverantörer kommer visas här.')}</span>
      )}

      {renderQuoteToggle()}

      {renderDateExtend()}

      {selectedSuppliers.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <BtButton
            onClick={onSaveSelectedSuppliersClick}
            loaderShow={showLoading}
          >
            {t(
              'saveButton',
              'Spara',
              { context: quoteVersions?.length ? (sendAutoBid ? 'withAutoBid' : 'withoutAutoBid') : undefined }
            )}
          </BtButton>
        </div>
      )}
    </>);
  },
  areEqual,
);

SelectedSupplierModule.displayName = 'SelectedSupplierModule';

export default SelectedSupplierModule;
